import {
  fetchAddItemToOrderByOutrightPurchase,
  fetchAddItemToOrderByPointsExchange,
  fetchAddItemToOrderByRegularOrder,
  fetchAddItemToOrderByShoppingTrolley,
  fetchGetPurchaseQuantity,
} from '@/services';
import uniMatomo from '@/services/matomo';
import { useUserStore } from '@/store';
import {
  MemberState,
  PlaceOrderType,
  PromotionType,
  PurchasePattern,
  WeChatPaymentType,
  type AnyObj,
  type AnyObjOrNull,
} from '@/types';
import type { ShoppingTrolley } from '@/types/global-business';
import { businessUtils } from '.';
import { commonUtils } from '../common';
import commodity from './commodity';
import sku from './sku';

/**
 * 获取会员价活动对应的规格优惠价格
 * @param skuId
 * @returns
 */
const getMemberPriceAmountBySkuId = (
  shoppingTrolleyPromotionResult: AnyObjOrNull,
  skuId: string
) => {
  if (!shoppingTrolleyPromotionResult || !skuId) {
    return 0;
  }

  const t1 = commonUtils.data.find(
    shoppingTrolleyPromotionResult,
    `promResult-orderLinePromResults-skuId|${skuId}`
  );
  if (t1) {
    // 原价单价
    const p1 = t1.price / t1.count;
    const t2 = commonUtils.data.find(t1, `discountDetails-type|memberPrice`);
    if (t2) {
      // 优惠单价
      const p2 = t2.discountAmount / t2.discountCount;
      const n = p1 - p2;
      return businessUtils.price.roundedDown(n);
    }
  }
  return 0;
};

/**
 * 转换商品数据
 * @param data - 商品数据
 * @param type - 商品数据结构类型
 * @returns
 */
const commodityHelper = (data: AnyObjOrNull, type: UnionKey.CommodityDataStructure) => {
  const isLines = type === 'lines' && !!data;
  const isNormal = type === 'normal' && data;

  return {
    /** 商品图片 */
    imgUrl: () => {
      if (isLines) {
        return (
          (data?.featuredAsset?.source ||
            data?.productVariant?.featuredAsset?.source ||
            data.productVariant.product.featuredAsset.source) ??
          ''
        );
      }
      if (isNormal) {
        return data.featuredAsset?.source ?? '';
      }
      return '';
    },
    /** 商品名称 */
    name: () => {
      if (isLines) {
        return data.productVariant.name ?? '';
      }
      if (isNormal) {
        return data.name ?? '';
      }
      return '';
    },
    /** 商品规格文本 */
    sku: () => {
      if (isLines) {
        return sku.getText(data.productVariant.options);
      }
      // normal类型没有已选择的规格
      return '';
    },
    /** 商品数量 */
    count: () => {
      if (isLines) {
        return (data.quantity || data.totalQuantity) ?? 0;
      }
      // normal类型没有已选择的数量
      return 1;
    },
    /** 商品是否启用 */
    enabled: () => {
      if (isLines) {
        return !!data.productVariant.product.enabled;
      }
      if (isNormal) {
        return data.featuredAsset?.source ?? '';
      }
      return true;
    },
    currentSkuExist: () => {
      if (isLines) {
        // 当前规格是否存在
        return !!data.productVariant;
      }
      if (isNormal) {
        return true;
      }
      return true;
    },
    /** 商品当前规格库存是否售罄 */
    currentSkuSoltOut: () => {
      if (isLines) {
        return !data.productVariant.isThereAnyStock;
      }
      if (isNormal) {
        return false;
      }
      return false;
    },
    /** 商品所有规格库存是否售罄 */
    allSkuSoltOut: () => {
      if (isLines) {
        return data.productVariant.product?.productTotalStock <= 0;
      }
      if (isNormal) {
        return false;
      }
      return false;
    },
    /** 是否定时开售中 */
    timedSale: () => {
      if (isLines) {
        return commodity.getTimedSaleConfig(data.productVariant.product.customFields).is;
      }
      if (isNormal) {
        return commodity.getTimedSaleConfig(data.customFields).is;
      }
      return false;
    },
    /** 是否指定会员限购 */
    productPurchasePermission: () => {
      return (
        businessUtils.commodity.specifyMemberPurchase(
          data?.productVariant?.product?.productPurchasePermission
        ) || false
      );
    },
    /** 是否积分兑换商品 */
    pointsExchange: () => {
      if (isLines) {
        return !!data?.productVariant?.pointExchange;
      }
      if (isNormal) {
        return !!data.pointsExchangeInfo;
      }
      return false;
    },
  };
};

/**
 * 商品模版
 */
const getCommodityStencil = (
  options: Partial<ShoppingTrolley.Commodity>,
  commodity?: {
    data: AnyObj;
    type: UnionKey.CommodityDataStructure;
  }
): ShoppingTrolley.Commodity => {
  const helper = commodityHelper(commodity?.data ?? null, commodity?.type ?? 'lines');

  const isPurchasePremium =
    commodity?.data?.customFields?.purchasePattern === PurchasePattern.PurchasePremium;

  const init = {
    // 是否勾选
    isChecked: false,
    // 是否启用
    isEnabled: helper.enabled(),
    // 当前规格是否存在
    isCurrentSkuExist: helper.currentSkuExist(),
    // 当前规格是否已售罄
    isCurrentSkuSoltOut: helper.currentSkuSoltOut(),
    // 所有规格是否已售罄
    isAllSkuSoltOut: helper.allSkuSoltOut(),
    // 是否定时开售中
    isTimedSale: helper.timedSale(),
    // 是否指定会员限购
    isProductPurchasePermission: helper.productPurchasePermission(),
    // 是否积分兑换商品
    isPointsExchange: helper.pointsExchange(),
    // 是否换购商品
    isPurchasePremium,
    // 是否展示会员价标签
    displayMemberTag: false,
    // 是否展示会员价限购提示
    displayToastMemberLimit: false,
    // 展示价
    displayAmount: 0,
    // 划线价
    lineThroughAmount: 0,
    // 预估到手价
    estimateAmount: -1,
    imgUrl: helper.imgUrl(),
    name: helper.name(),
    sku: helper.sku(),
    count: helper.count(),
    skuId: '',
    _source: null,
  };

  return {
    ...init,
    ...options,
  };
};

/**
 * 计算会员商品价格信息（会员价标识、会员价、原价、划线价、预估到手价）
 * @param commodity - 商品数据
 * @param promotionResult - 活动数据（购物车、预下单）
 * @param userMemberInfo - 用户会员数据
 * @param useScene - 使用场景 normal：正常商品 lines：订单项商品
 */
const getCommodityAmounts = (
  commodity: ShoppingTrolley.Commodity,
  promotionResult: { shoppingTrolley?: AnyObjOrNull; outrightPurchase?: AnyObjOrNull } | null,
  userMemberInfo: AnyObjOrNull,
  useScene: string = 'line'
) => {
  const shoppingTrolleyPromotionResult = promotionResult?.shoppingTrolley ?? null;
  const outrightPurchasePromotionResult = promotionResult?.outrightPurchase ?? null;

  const config = {
    /** 展示价 */
    displayAmount: 0,
    /** 划线价 */
    lineThroughAmount: 0,
    /** 预估到手价 */
    estimateAmount: -1,
    /** 换购价 */
    // purchasePremiumAmount: 0,
    /** 是否展示会员价标签 */
    displayMemberTag: false,
    /** 是否展示会员价标签 */
    displayBlindBoxTag: false,
    /** 是否taost会员上限 */
    displayToastMemberLimit: false,
  };

  // 商品原价
  // 备注：2024-04-11 - 商品原价 = 所有规格的最低价。
  let _unitAmount = 0,
    // 商品划线价
    _lineThroughAmount = 0,
    // 是否vip商品
    isVipProduct = false,
    // 会员价活动最低优惠展示价
    memberPriceAmount = 0,
    // 是否参与会员价活动
    isIncludeMemberPirceActivity = false;

  const _source = commodity._source;

  // 是否会员用户
  const isMemberUser = userMemberInfo?.state === MemberState.normal;
  // 是否勾选
  const isChecked = commodity.isChecked;

  // 根据数据结构区分价格信息获取
  if (useScene === 'line') {
    const customFields = _source.productVariant.product.customFields;
    _unitAmount = _source.unitPrice ?? 0;
    _lineThroughAmount = customFields.markingPrice ?? 0;
    isVipProduct = !!customFields.isVipProduct;
    // 在订单项场景时，取对应规格的优惠价格
    // 售后、订单详情场景默认是勾选，无需执行计算
    memberPriceAmount = +getMemberPriceAmountBySkuId(
      shoppingTrolleyPromotionResult,
      _source.productVariant.id
    );
    isIncludeMemberPirceActivity = memberPriceAmount > 0;
  } else {
    const customFields = _source.customFields;
    const memberPriceActivity = _source?.memberPriceActivityAmount;

    _unitAmount = customFields.price ?? 0;
    _lineThroughAmount = customFields.markingPrice ?? 0;
    isVipProduct = !!customFields.isVipProduct;
    memberPriceAmount = memberPriceActivity?.minMemberPriceAmount;
    isIncludeMemberPirceActivity = memberPriceActivity?.minDiscount > 0;
  }

  // 设置默认价格信息
  // 展示价 = 商品原价
  config.displayAmount = _unitAmount;
  // 划线价 = 商品划线价
  config.lineThroughAmount = _lineThroughAmount;

  // 执行未勾选操作
  const exeUncheckedHandler = () => {
    const orderLinePromResults =
      shoppingTrolleyPromotionResult?.promResult?.orderLinePromResults ?? [];
    // 查找是否有盲盒优惠
    orderLinePromResults.forEach((olpr: AnyObj) => {
      if (olpr.skuId === commodity.skuId && !commodity.isPurchasePremium) {
        // console.log(`执行未勾选操作-orderLinePromResults`, { orderLinePromResults, olpr });
        config.displayBlindBoxTag = !!olpr.discountDetails.find(
          (k: AnyObj) => k.type === PromotionType.BlindBox
        );
      }
    });

    // 是会员用户
    if (isMemberUser) {
      // 最低优惠价格
      let lowsetPrice = 0;
      // 参与了会员价活动
      if (isIncludeMemberPirceActivity) {
        lowsetPrice = memberPriceAmount;
      } else {
        // 没有参与，则判断是否会员卡折扣商品
        if (isVipProduct) {
          lowsetPrice = +businessUtils.price.roundedDown(
            (_unitAmount * userMemberInfo.membershipPlan.rightsDiscount.discountRate) / 100
          );
        }
      }

      // 存在最低优惠价，且 如果参与了会员价活动，或者 是会员卡折扣商品
      if (lowsetPrice > 0 && (isIncludeMemberPirceActivity || isVipProduct)) {
        // 展示会员价标签
        config.displayMemberTag = true;
        // 展示价 = 最低优惠价
        config.displayAmount = lowsetPrice;
        // 划线价 = 商品原价
        config.lineThroughAmount = _unitAmount;
      }
    }
  };

  if (isChecked) {
    if (!outrightPurchasePromotionResult) {
      // 已勾选 - 没有活动配置信息，依然执行未勾选操作 (不含分组的剩余商品)
      exeUncheckedHandler();
    } else {
      // 已勾选 - 有活动配置信息 (分组商品)
      // 查询订单项优惠数据
      const orderLinePromResults =
        outrightPurchasePromotionResult?.promResult?.orderLinePromResults ?? [];

      const promLineResults = outrightPurchasePromotionResult?.promResult?.promLineResults ?? [];

      orderLinePromResults.forEach((olpr: AnyObj) => {
        // 判断查询目标优惠数据
        if (olpr.skuId === commodity.skuId && !commodity.isPurchasePremium) {
          // 预估到手价 = (优惠原价 - 优惠总金额) / 优惠数量
          config.estimateAmount = +businessUtils.price.roundedUp(
            (olpr.price - olpr.discountAmount) / olpr.count
          );

          // console.log(`执行勾选操作-orderLinePromResults`, { orderLinePromResults, olpr });
          // 查找是否有盲盒优惠
          config.displayBlindBoxTag = !!olpr.discountDetails.find(
            (k: AnyObj) => k.type === PromotionType.BlindBox
          );

          // 查找是否有会员优惠，优先会员价活动，其次会员卡折扣
          let memberDiscount = olpr.discountDetails.find(
            (k: AnyObj) => k.type === PromotionType.memberPrice
          );
          if (!memberDiscount) {
            memberDiscount = olpr.discountDetails.find(
              (k: AnyObj) => k.type === PromotionType.member
            );
          }

          if (memberDiscount) {
            // 展示会员价标签
            config.displayMemberTag = true;
            // 展示价 = (优惠原价 / 会员优惠总数量 ）* 会员优惠比例discount
            const promLineFilters = promLineResults
              ?.filter((it: AnyObject) => it.type === 'member' || it.type === 'memberPrice')
              ?.map((it: AnyObject) => it.orderLines)
              ?.flat();
            const orderLineItem = promLineFilters.find(
              (it: AnyObject) => it.orderLineId === olpr.orderLineId
            );
            if (orderLineItem) {
              config.displayAmount = +businessUtils.price.roundedDown(
                (olpr.price / orderLineItem.discountCount) * (orderLineItem.discount / 100)
              );
            } else {
              console.log(`<会员优惠计算有误，请检查>`, {
                promLineResults,
                promLineFilters,
                orderLineItem,
              });
              config.displayAmount = 0;
            }
            // 划线价 = 商品原价
            config.lineThroughAmount = _unitAmount;
          } else {
            const includePackageDiscount = olpr.discountDetails.find(
              (k: AnyObj) => k.type === PromotionType.packageDiscount
            );
            // 没有会员优惠
            // 判断场景 - 提示会员上限提示
            if (
              // 是会员
              isMemberUser &&
              // 是会员商品
              isVipProduct &&
              // 查找是否有打包一口价的优惠 - 需没有
              !includePackageDiscount &&
              // 没有盲盒优惠
              !config.displayBlindBoxTag
            ) {
              config.displayToastMemberLimit = true;
            }

            // 不展示会员价标签
            config.displayMemberTag = false;
            // 展示价 = 商品原价
            config.displayAmount = _unitAmount;
            // 划线价 = 商品划线价
            config.lineThroughAmount = _lineThroughAmount;
          }
        } else {
          // 没有目标优惠数据 - 服务端bug
        }
      });
    }
  } else {
    // 未勾选 => 不会存在活动配置信息，统一根据商品属性配置
    exeUncheckedHandler();
  }

  // 如果是换购商品，额外计算处理换购价和划线价
  if (commodity.isPurchasePremium) {
    const items = outrightPurchasePromotionResult?.promResult.gifts
      .filter((it: AnyObj) => it.giftType === 'mark_up')
      .map((it: AnyObj) => it.items)
      .flat();
    const itemTarget = items?.find(
      (it: AnyObj) => it.productId === commodity._source.productVariant.product.id
    );
    if (itemTarget) {
      // config.purchasePremiumAmount = itemTarget.giftPrice;
      // 展示价 = 对应活动的优惠价
      config.displayAmount = itemTarget.giftPrice;
      // 划线价 = 对应商品sku的原价
      config.lineThroughAmount = commodity._source.productVariant.price;
    }
  }

  return config;
};

/**
 * 根据接口数据获取转换后的购物车分组卡片配置信息
 * @param shoppingTrolley
 * @param shoppingTrolleyPromotionResult
 * @param outrightPurchasePromotionResult
 * @param extraOptions
 */
export const getCartGroupCardConfig = async (
  shoppingTrolley: any,
  shoppingTrolleyPromotionResult: any,
  outrightPurchasePromotionResult: any,
  extraOptions?: {
    freeGiftByOrder: AnyObject[];
    purchasePremiumByOrder: AnyObject[];
  }
) => {
  if (!shoppingTrolley) return [];

  // 如果购物车活动配置没有数据则直接返回空
  if (!shoppingTrolleyPromotionResult?.promResult) return [];

  const freeGiftByOrder = extraOptions?.freeGiftByOrder ?? [];
  const purchasePremiumByOrder = extraOptions?.purchasePremiumByOrder ?? [];

  // 获取用户会员信息
  const userMemberInfo = await useUserStore().getUserMemberInfo();

  // 活动
  const promLineResultsRenders: ShoppingTrolley.CardGroup[] =
    shoppingTrolleyPromotionResult?.promResult.promLineResults.map((stpsItem: AnyObj) => {
      // 活动目标数据 购物车活动｜预下单活动 => 是否有商品勾选
      let promotionTarget = stpsItem;

      // 标记：商品存在，只是不显示在当前分组
      let whetherCommodityExistButNotDisplayInThisGroup = false;

      // 预下单活动里的数据信息
      const outrightPurchaseTarget =
        outrightPurchasePromotionResult?.promResult.promLineResults.find((oit: AnyObj) => {
          return stpsItem.promInstanceId === oit.promInstanceId;
        });

      // 处理商品配置
      const commodityListHandler = () => {
        // 正常商品列表
        let commodityList: ShoppingTrolley.Commodity[] = [];
        // 失效商品列表
        let invalidList: ShoppingTrolley.Commodity[] = [];

        // 查询商品
        stpsItem.orderLines.forEach((orderLine: AnyObj) => {
          const commodity = shoppingTrolley.lines.find(
            (st: AnyObj) => st.id === orderLine.orderLineId
          );
          // 商品存在
          if (commodity) {
            // 是否显示在当前分组
            if (orderLine.displayInThisGroup) {
              // 默认false
              let isChecked = false;

              // 查询目标订单的商品是否在预下单中，是则勾选
              outrightPurchasePromotionResult?.promResult.promLineResults.forEach((i: any) => {
                const check = i.orderLines.find(
                  (k: any) => k.skuId === commodity.productVariant.id
                );
                if (check) isChecked = true;
              });

              // 获取商品模版
              const stencil = getCommodityStencil(
                {
                  isChecked,
                  skuId: commodity.productVariant.id,
                  _source: commodity,
                },
                {
                  data: commodity,
                  type: 'lines',
                }
              );

              // 根据活动处理商品价格等信息
              const amounts = getCommodityAmounts(
                stencil,
                {
                  outrightPurchase: outrightPurchasePromotionResult,
                  shoppingTrolley: shoppingTrolleyPromotionResult,
                },
                userMemberInfo,
                'line'
              );

              const result = {
                ...stencil,
                ...amounts,
              };

              // 是否失效
              const isInvalid = result.isAllSkuSoltOut || !result.isEnabled;
              if (isInvalid) {
                invalidList.push(result);
              }

              // 不是加价购 且 不是失效商品 则加入商品列表
              if (!result.isPurchasePremium && !isInvalid) {
                commodityList.push(result);
              }
            } else {
              // 不显示在当前分组
              const isInvalid =
                !commodity.productVariant.isThereAnyStock ||
                !commodity.productVariant.product.enabled;

              if (!isInvalid) {
                whetherCommodityExistButNotDisplayInThisGroup = true;
              }
            }
          } else {
            // 商品不存在
            console.log(`在当前活动数据里-未找到商品`);
          }
        });

        return {
          commodityList,
          invalidList,
        };
      };

      // 商品数据
      const { commodityList, invalidList } = commodityListHandler();

      // 是否存在商品
      const isExistCommodity = !!commodityList.length;

      // 判断商品是否存在勾选
      const isSomeChecked = commodityList.some((it) => it.isChecked);
      if (isSomeChecked) {
        // 预下单活动活动存在则替换
        if (outrightPurchaseTarget) {
          promotionTarget = outrightPurchaseTarget;
        }
      }

      // 是否分组；当没有商品的时候，手动设置为null，区分服务端的false
      const isGroup = !isExistCommodity ? null : promotionTarget.shouldGroup;

      // 是否勾选分组
      const isChecked = isGroup ? commodityList.every((it) => it.isChecked) : false;

      // ------------------------------- 处理加价购  -------------------------------
      // 是否为加价购活动类型
      const isPurchasePremiumType = promotionTarget.type === PromotionType.purchaseAtAPremium;
      // 是否存在换购数据
      const hasPurchasePremiumLength =
        promotionTarget.gifts?.items?.length && purchasePremiumByOrder?.length;

      if (isPurchasePremiumType && hasPurchasePremiumLength) {
        promotionTarget.gifts.items.forEach((item: AnyObject) => {
          // 找到对应的换购商品
          const target = purchasePremiumByOrder.find(
            (it: AnyObject) => it.product.id === item.productId
          );

          if (target) {
            // 替换赠品字段 - 需要取对应的预下单活动的赠品里的字段
            const promLineGiftItem = outrightPurchaseTarget?.gifts?.items.find(
              (it: AnyObject) => it.productId === target.product.id
            );
            if (promLineGiftItem) {
              item.isAvailable = promLineGiftItem.isAvailable;
              item.selected = promLineGiftItem.selected;
              item.minimumStr = promLineGiftItem.minimumStr;
              item.skuId = promLineGiftItem.skuId;
            }

            // 换购商品处理
            item._commodityHandler = {
              name: target.product.name,
              sku: '请选择',
              imgUrl: target.product.featuredAsset?.source,
              amount: item.giftPrice,
              lineThroughAmount: 0,
              count: 0,
            };

            let variant: AnyObject | null = null;

            // 默认选中
            if (item.skuId) {
              variant = target.product.variants.find((it: AnyObject) => it.id === item.skuId);
            } else {
              // 如果没有默认，则选择规格的第一个
              variant = target.product.variants[0];
            }

            // 规格赋值
            if (variant) {
              if (variant.featuredAsset?.source) {
                item._commodityHandler.imgUrl = variant.featuredAsset?.source;
              }
              item._commodityHandler.lineThroughAmount = variant.price;
              item._commodityHandler.sku = businessUtils.sku.getText(variant.options);
            }

            //  缓存源数据
            item._source = target.product;
          }

          item._randomId = commonUtils.data.getRandomId(26);
        });
      }

      // ------------------------------- 处理赠品  -------------------------------
      // 是否为赠品活动类型
      const isGiftType = [
        PromotionType.selectiveGift,
        PromotionType.fullDiscountPresent,
        PromotionType.actuallyPaid,
      ].includes(promotionTarget.type);
      // 是否存在赠品数据
      const hasGiftLength = promotionTarget.gifts?.items?.length && freeGiftByOrder?.length;
      // 获取活动里的赠品数据 - 预下单活动里的赠品数据 - 当前活动里的赠品数据
      const giftItemStencils =
        outrightPurchaseTarget?.gifts?.items || promotionTarget?.gifts?.items || [];

      if (isGiftType && hasGiftLength) {
        promotionTarget.gifts.items = giftItemStencils.map((item: AnyObject) => {
          // 找到对应的赠品
          const target = freeGiftByOrder.find(
            (commodity: AnyObject) => commodity.id === item.giftId
          );

          if (target) {
            // 替换赠品字段 - 需取对应的预下单活动的赠品里的字段
            const promLineGiftItem = outrightPurchaseTarget?.gifts?.items.find(
              (it: AnyObject) =>
                // 判断是否同一商品 且 是同一阶梯
                it.productId === target.product.id && item.ladderLevel === it.ladderLevel
            );
            if (promLineGiftItem) {
              item.isAvailable = promLineGiftItem.isAvailable;
              item.selected = promLineGiftItem.selected;
              item.minimumStr = promLineGiftItem.minimumStr;
              item.skuId = promLineGiftItem.skuId;
            }

            item._commodityHandler = {
              name: target.product.name,
              sku: '请选择',
              imgUrl: target.product.featuredAsset?.source,
              amount: 0,
              lineThroughAmount: target.product.customFields.markingPrice ?? 0,
              count: item.count,
            };

            let variant: AnyObject | null = null;

            // 默认选中
            if (item.skuId) {
              variant = target.product.variants.find((it: AnyObject) => it.id === item.skuId);
            } else {
              // 如果没有默认，则选择规格的第一个
              variant = target.product.variants[0];
            }

            // 规格赋值
            if (variant) {
              if (variant.featuredAsset?.source) {
                item._commodityHandler.imgUrl = variant.featuredAsset?.source;
              }
              // 赠品商品价格显示0
              item._commodityHandler.amount = 0;
              // 赠品划线价显示赠品原价
              item._commodityHandler.lineThroughAmount = variant.price;
              item._commodityHandler.sku = businessUtils.sku.getText(variant.options);
            }

            //  缓存源数据
            item._source = target.product;
          }

          item._randomId = commonUtils.data.getRandomId(26);

          return item;
        });
      }

      // 优惠商品排序
      // 用户选择后，则把用户已选择的放第一位。
      // 用户没有选择，则按照服务端的排序。
      const giftList = promotionTarget.gifts?.items || [];
      giftList.sort((a: AnyObject, b: AnyObject) => Number(b.selected) - Number(a.selected));

      // 判断是否全部商品都已售罄或失效
      let whetherAllCommodityInvalid = false;

      if (!whetherCommodityExistButNotDisplayInThisGroup) {
        whetherAllCommodityInvalid =
          commodityList.length > 0 &&
          commodityList.every((it) => {
            return it.isAllSkuSoltOut || !it.isEnabled;
          });
      }

      const item: ShoppingTrolley.CardGroup = {
        commodityList: whetherAllCommodityInvalid ? [] : commodityList,
        invalidList,
        isGroup: whetherAllCommodityInvalid ? null : isGroup,
        isChecked: whetherAllCommodityInvalid ? null : isChecked,
        giftList: whetherAllCommodityInvalid ? [] : giftList,
        whetherCommodityExistButNotDisplayInThisGroup,
        _source: promotionTarget,
      };

      return item;
    });

  // 非活动
  const leftOrderLinesRenders: ShoppingTrolley.CardGroup[] =
    shoppingTrolleyPromotionResult?.promResult.leftOrderLines.map((stpsItem: AnyObj) => {
      // 处理商品配置
      const commodityListHandler = () => {
        // 正常商品列表
        let commodityList: ShoppingTrolley.Commodity[] = [];
        // 失效商品列表
        let invalidList: ShoppingTrolley.Commodity[] = [];

        // 查询商品
        const commodity = shoppingTrolley.lines.find(
          (stItem: AnyObj) => stItem.id === stpsItem.lineId
        );

        // 商品存在
        if (commodity) {
          let isChecked = false;
          // 查询目标订单的商品是否在预下单中，是则勾选(存在活动不叠加会员情况，取消会员优惠会导致原非活动商品变为活动商品，故需在orderLinePromResults中遍历寻找对应的skuId)
          outrightPurchasePromotionResult?.promResult.orderLinePromResults.forEach((i: any) => {
            const check = i.skuId === commodity.productVariant.id;
            if (check) isChecked = true;
          });

          // 获取商品模版
          const stencil = getCommodityStencil(
            {
              isChecked,
              skuId: commodity.productVariant.id,
              _source: commodity,
            },
            {
              data: commodity,
              type: 'lines',
            }
          );

          // 根据活动处理商品价格等信息
          const amounts = getCommodityAmounts(
            stencil,
            {
              outrightPurchase: outrightPurchasePromotionResult,
              shoppingTrolley: shoppingTrolleyPromotionResult,
            },
            userMemberInfo,
            'line'
          );

          const result = {
            ...stencil,
            ...amounts,
            // estimateAmount: -1,
          };

          // 是否失效
          const isInvalid = result.isAllSkuSoltOut || !result.isEnabled;
          if (isInvalid) {
            invalidList.push(result);
          } else {
            commodityList.push(result);
          }
        }
        return {
          commodityList,
          invalidList,
        };
      };

      // 商品数据
      const { commodityList, invalidList } = commodityListHandler();

      // 判断是否全部商品都已售罄或失效
      const whetherAllCommodityInvalid = commodityList.every(
        (it) => it.isAllSkuSoltOut || !it.isEnabled
      );

      const item: ShoppingTrolley.CardGroup = {
        commodityList: whetherAllCommodityInvalid ? [] : commodityList,
        invalidList,
        giftList: [],
        isGroup: null,
        isChecked: null,
        whetherCommodityExistButNotDisplayInThisGroup: false,
        _source: stpsItem,
      };

      return item;
    });

  return [...promLineResultsRenders, ...leftOrderLinesRenders];
};

export default {
  getCommodityAmounts,
  getCommodityStencil,
  getCartGroupCardConfig,

  /**
   * 校验商品限购数量
   * @param productId - 商品id
   */
  getPurchaseLimit: async (
    productId: string,
    type: PlaceOrderType = PlaceOrderType.regularOrder,
    isIncludeCurrentOrder?: boolean
  ) => {
    let _isIncludeCurrentOrder = isIncludeCurrentOrder;
    if (!_isIncludeCurrentOrder && PlaceOrderType.regularOrder) {
      _isIncludeCurrentOrder = false;
    }

    const res = await fetchGetPurchaseQuantity(productId, type, _isIncludeCurrentOrder);

    // console.log(`用户侧对正常商品限购数量`, res);

    if (res === 0)
      return {
        isLimit: true,
        count: 0,
      };
    return {
      isLimit: false,
      count: res,
    };
  },
  /**
   * 根据优惠类型信息判断是否包含会员相关类型
   * @param data
   * @returns
   */
  isIncludeMember: (data: AnyObj) => {
    let target = null;
    if (data) {
      // 优先会员价活动
      target = data.find((it: AnyObj) => it.type === PromotionType.memberPrice);
      if (!target) {
        // 其次会员卡折扣
        target = data.find((it: AnyObj) => it.type === PromotionType.member);
      }
    }
    return target;
  },
  /**
   * 获取商品原价合计金额
   * @param list - 订单数据
   */
  totalOriginalPrice: (list: AnyObj[]) => {
    let n = 0;
    list.forEach((it) => {
      let p = it.unitPrice;
      // 单件总价
      const singleTotal = p * it.quantity;
      // 总和相加
      n = singleTotal + n;
    });
    return businessUtils.price.roundedDown(n);
  },
  /**
   * 转换优惠类型详情
   * @param discountByTypes
   */
  getDiscounts: (discountByTypes: AnyObj[]) => {
    const mapPreferintialTypeToText = (type: string) => {
      const config: AnyObj = {
        [PromotionType.coupon]: '优惠券',
        [PromotionType.member]: '会员折扣',
        [PromotionType.memberPrice]: '商品会员折扣',
        [PromotionType.fullDiscountPresent]: '满减送',
        [PromotionType.discountActivity]: '第X件Y折',
        [PromotionType.purchaseAtAPremium]: '优惠换购',
        [PromotionType.packageDiscount]: '打包一口价',
        [PromotionType.BlindBox]: '盲盒优惠折扣',
      };
      return config[type];
    };

    const _discountByTypes: { type: any; text: any; amount: any }[] = [];

    // 会员总优惠
    let memberRelatedTotal = 0;

    discountByTypes.forEach((it, index) => {
      // 对会员相关的优惠信息进行处理
      if (it.type === PromotionType.memberPrice || it.type === PromotionType.member) {
        memberRelatedTotal = memberRelatedTotal + it.discountAmount;
      } else {
        // 剔除积分、购物金抵扣、购物金可得
        if (
          ![
            PromotionType.pointsExchange,
            PromotionType.shoppingCreditsDeduction,
            PromotionType.shoppingCreditsClaim,
          ].includes(it.type)
        ) {
          // 配置上剔除会员相关优惠
          _discountByTypes.push({
            type: it.type,
            text: mapPreferintialTypeToText(it.type),
            amount: it.discountAmount,
          });
        }
      }

      // 如果是添加最后一个时，额外新增一个配置
      if (index === discountByTypes.length - 1) {
        _discountByTypes.push({
          type: PromotionType._memberRelatedTotal,
          text: `商品会员折扣`,
          amount: +businessUtils.price.roundedDown(memberRelatedTotal),
        });
      }
    });

    // console.log(`优惠类型详情`, {
    //   _discountByTypes,
    //   discountByTypes,
    // });

    return _discountByTypes;
  },
  /**
   * 获取微信支付的参数
   * @param other - 其他参数
   * @returns
   */
  getWechatPaymentVariables: (other: AnyObj = {}) => {
    let variables = { ...other };
    // #ifdef MP
    variables = { ...variables, paymentType: WeChatPaymentType.wechatProgram };
    // #endif
    // #ifdef H5
    variables = { ...variables, paymentType: WeChatPaymentType.wechatOfficialAccounts };
    // #endif
    return variables;
  },
  /**
   * matomo上报添加购物车/直接下单
   * @param variables - 请求参数{productVariantId, quantity}
   * @param type - 请求类型：shoppingTrolley/regularOrder/outrightPurchase
   */
  matomoAddItemToOrderByOrderType: async (variables: AnyObj, type: PlaceOrderType) => {
    try {
      // matomo上报加购
      uniMatomo.trackEvent('Preorderbutton', 'Click', 'Click_Count', 1);

      variables = { ...variables, type };

      const fetchConfig = {
        [PlaceOrderType.shoppingTrolley]: fetchAddItemToOrderByShoppingTrolley,
        [PlaceOrderType._regularOrderToShoppingTrolley]: fetchAddItemToOrderByShoppingTrolley,
        [PlaceOrderType.outrightPurchase]: fetchAddItemToOrderByOutrightPurchase,
        [PlaceOrderType.regularOrder]: fetchAddItemToOrderByRegularOrder,
        [PlaceOrderType.pointsExchange]: fetchAddItemToOrderByPointsExchange,
      };

      return fetchConfig[type](variables);
    } catch (error) {
      console.log(error);
    }
  },
};
